/*
 * "Meet Ada" — the introduction, as one continuous move from the conversation.
 *
 * The rule the whole thing is built on: nothing appears out of nowhere. The
 * mark in the header is the same object throughout — it is measured where it
 * actually sits, a stand-in is placed exactly on top of it, and that stand-in
 * is what turns, travels, blooms and finally settles behind the words as the
 * page's own halo. An overlay that faded in over the conversation would be a
 * different screen; this is the screen the operator was already looking at,
 * opening up.
 *
 * Three things make it read as one move rather than four:
 *
 *  - The ring never re-rotates from zero. One keyframe carries it from the
 *    header to the halo, and it lands on a whole number of turns, so the idle
 *    spin that follows picks up exactly where it stopped. A restarted rotation
 *    snaps the gradient back a few degrees, and once seen it cannot be unseen.
 *
 *  - Only transform and opacity are animated. Position and size are expressed
 *    as a translate and a scale off one fixed 260px orb, so every frame stays
 *    on the compositor and the travel does not judder on a busy console.
 *
 *  - The dissolve is a softening, not a disappearance. The crisp ring blurs
 *    and dims into the aura the content then sits on, so the object survives
 *    the transition and the eye follows it into the reveal.
 *
 * Reduced motion is honoured properly: no travel, no spin, no bloom — the
 * reveal simply fades in. It is a cosmetic page, and nobody should have to
 * endure it to reach the assistant.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { FiArrowRight, FiX, FiChevronDown } from "react-icons/fi";
import { ASSISTANT_NAME } from "../appView";

/* The orb's real pixel size. Everything else is a scale off this, so the ring
   is drawn once at full fidelity and never re-rasterised mid-flight. */
const ORB = 260;

/* The turn, the bloom. Reveal runs on CSS delays from there.
 *
 * Launch is a single frame, not a beat. It used to hold for 420ms while the
 * backdrop came up, which read as: the page goes black, and THEN something
 * starts moving. The mark has to be visibly growing out of its own place before
 * the conversation has finished receding — so launch exists only to paint the
 * stand-in at the origin, and everything else happens over the top of a
 * backdrop that is still arriving. */
const PHASES = [
  ["launch", 0],   // one painted frame, not a beat — see the effect below
  ["travel", 1250],
  ["bloom", 800],
];

const reduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export default function AdaIntro({ originRef, onClose }) {
  // launch → travel → bloom → reveal, or straight to reveal for reduced motion.
  const [phase, setPhase] = useState(() => (reduced() ? "reveal" : "launch"));
  const [closing, setClosing] = useState(false);
  // Which example question is showing, and whether the detail is open.
  const [qi, setQi] = useState(0);
  const [more, setMore] = useState(false);
  const [origin, setOrigin] = useState(null);
  const rootRef = useRef(null);
  const closeRef = useRef(null);

  /* Where the header mark actually is, read before the first paint so the
     stand-in is never seen anywhere else. Falls back to the top-left corner if
     the mark is not mounted, which is wrong but not broken. */
  useLayoutEffect(() => {
    const el = originRef && originRef.current;
    const r = el ? el.getBoundingClientRect() : null;
    setOrigin(r
      ? { x: r.left + r.width / 2, y: r.top + r.height / 2, size: r.width }
      : { x: 120, y: 34, size: 26 });
  }, [originRef]);

  /* Advance the sequence.
   *
   * The launch→travel flip is made on the second animation frame rather than on
   * a timer. A transition needs one painted frame at the starting value before
   * it will run, and a timer buys that frame at the cost of a visible pause —
   * a third of a second of nothing after the press, which reads as a click that
   * did not register. Two frames is the smallest wait that still paints.
   *
   * Everything after is timed, and cleared on unmount so a fast close cannot
   * land a phase change on a component that is already going away. */
  useEffect(() => {
    if (reduced()) return undefined;
    const timers = [];
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setPhase("travel");
        let at = 0;
        PHASES.slice(1).forEach(([, ms], i) => {
          at += ms;
          const next = PHASES[i + 2] ? PHASES[i + 2][0] : "reveal";
          timers.push(setTimeout(() => setPhase(next), at));
        });
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      timers.forEach(clearTimeout);
    };
  }, []);

  const close = useCallback(() => {
    if (closing) return;
    if (reduced()) { onClose(); return; }
    setClosing(true);
    // The orb goes home the way it came; the overlay leaves with it.
    setTimeout(onClose, 620);
  }, [closing, onClose]);

  // Escape closes, and focus is parked somewhere sensible once there is
  // something to read rather than stolen while the orb is still travelling.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  useEffect(() => {
    if (phase === "reveal" && closeRef.current) closeRef.current.focus();
  }, [phase]);

  /* The questions turn over on their own, but only once there is something to
     read them against, and never when motion is unwelcome. */
  useEffect(() => {
    if (phase !== "reveal" || reduced()) return undefined;
    const t = setInterval(() => setQi((i) => (i + 1) % QUESTIONS.length), 3200);
    return () => clearInterval(t);
  }, [phase]);

  if (!origin) return null;

  const vw = window.innerWidth / 2;
  const vh = window.innerHeight / 2;
  // Home is the header mark; away is dead centre. The stand-in starts and ends
  // at home, and the class decides which of the two is in force.
  const style = {
    "--home-x": `${origin.x - vw}px`,
    "--home-y": `${origin.y - vh}px`,
    "--home-s": origin.size / ORB,
    "--orb": `${ORB}px`,
  };

  const state = closing ? "closing" : phase;

  return (
    <div
      ref={rootRef}
      className={`ai-root is-${state}${more ? " has-more" : ""}`}
      style={style}
      role="dialog"
      aria-modal="true"
      aria-label={`Meet ${ASSISTANT_NAME}`}
    >
      <Styles />

      {/* The conversation, still there, pushed back rather than replaced. */}
      <button className="ai-scrim" onClick={close} aria-label="Close" tabIndex={-1} />

      {/* Two slow drifting fields. They are the only thing in the page that
          moves once it has settled, and they move too slowly to notice
          directly — which is the point of them. */}
      <div className="ai-field ai-field--a" aria-hidden="true" />
      <div className="ai-field ai-field--b" aria-hidden="true" />

      {/* The margins, doing something. See Lanes. */}
      <div className="ai-lanes ai-lanes--l" aria-hidden="true"><Lanes id="l" /></div>
      <div className="ai-lanes ai-lanes--r" aria-hidden="true"><Lanes id="r" /></div>

      {/* The mark from the header: one object, four states. */}
      <div className="ai-orb" aria-hidden="true">
        <span className="ai-halo" />
        <span className="ai-ring" />
        <span className="ai-orbit ai-orbit--1" />
        <span className="ai-orbit ai-orbit--2" />
        <span className="ai-orbit ai-orbit--3" />
        <img className="ai-logo" src={`${process.env.PUBLIC_URL}/sidebar-logo-w.png`} alt="" />
      </div>

      <div className="ai-content">
        {/* The brand carries the weight here — someone meeting this for the
            first time has to leave knowing whose it is, and "Weavian" is the
            half that does that work. "Edge Console" only says where they are. */}
        <p className="ai-eyebrow" style={{ "--d": "0ms" }}>
          <span className="ai-eyebrow-brand">Weavian</span>
          <span className="ai-eyebrow-rest">Edge Console</span>
        </p>

        <h1 className="ai-name" style={{ "--d": "90ms" }}>{ASSISTANT_NAME}</h1>

        <p className="ai-tagline" style={{ "--d": "190ms" }}>
          The AI assistant for <span>Network Intelligence</span>
        </p>

        <div className="ai-rule" style={{ "--d": "300ms" }} aria-hidden="true" />

        {/* What she can be asked, one at a time.
            Three paragraphs of capability was a wall to read, and nobody
            standing in front of a new product reads a wall. A question they
            recognise as their own problem does the same job in a second, and
            they can have the paragraphs below if they want them. */}
        <div className="ai-asks" style={{ "--d": "400ms" }}>
          <span className="ai-asks-lead">Ask her</span>
          <span className="ai-asks-rota">
            <span className="ai-asks-q" key={qi}>{QUESTIONS[qi]}</span>
          </span>
        </div>

        <div className="ai-actions" style={{ "--d": "520ms" }}>
          <button ref={closeRef} className="ai-go" onClick={close}>
            <span className="ai-go-sheen" aria-hidden="true" />
            <span>Start a conversation</span>
            <FiArrowRight size={17} />
          </button>
        </div>

        <button
          className={`ai-more${more ? " is-open" : ""}`}
          style={{ "--d": "620ms" }}
          onClick={() => setMore((v) => !v)}
          aria-expanded={more}
        >
          What she actually does
          <FiChevronDown size={15} />
        </button>

        {more && (
          <ul className="ai-pillars">
            {PILLARS.map((p, i) => (
              <li key={p.title} className="ai-pillar" style={{ "--d": `${i * 80}ms` }}>
                <span className="ai-pillar-no">{String(i + 1).padStart(2, "0")}</span>
                <strong>{p.title}</strong>
                <span className="ai-pillar-body">{p.body}</span>
              </li>
            ))}
            <li className="ai-foot">
              Ada runs on the appliance itself. Only the model call ever leaves the box.
            </li>
          </ul>
        )}
      </div>

      <button className="ai-skip" onClick={close} aria-label="Close introduction">
        <FiX size={16} />
      </button>
    </div>
  );
}

/* The sides, doing something true.
 *
 * Four lanes running the height of the page, each carrying a slow stream of
 * packets, with the bright one moving from lane to lane on a long cycle. That
 * is the product in one picture: traffic spread over several links at once,
 * and which link carries it changing on a schedule rather than staying put —
 * bonding, and the rotation the concealment layer is built on.
 *
 * Drawn as two strokes per lane: a dim wire that is always there, and a dashed
 * stroke whose offset animates, which is a stream of moving dots without a line
 * of JavaScript. Worth being straight about the cost: stroke-dashoffset is not
 * a compositor property, so each frame repaints the strokes. Eight thin paths
 * on a page the operator opens deliberately and leaves in under a minute is a
 * fair trade; the same trick behind the live console would not be.
 */
const LANE_PATHS = [
  "M 62 -40 C 152 130, 8 300, 98 470 C 172 618, 40 782, 122 1044",
  "M 152 -40 C 58 142, 202 322, 118 500 C 48 660, 192 820, 108 1044",
  "M 18 -40 C 112 200, 28 420, 152 620 C 222 758, 118 900, 172 1044",
  "M 212 -40 C 128 160, 222 380, 128 560 C 58 720, 182 880, 88 1044",
];

/* Where the lanes are worth marking — a handful only. A node on every bend
   turns a quiet background into a circuit diagram. */
const LANE_NODES = [
  [98, 470], [118, 500], [152, 620], [128, 560], [122, 210], [108, 830],
];

function Lanes({ id }) {
  const grad = `ai-lane-${id}`;
  return (
    <svg viewBox="0 0 230 1000" preserveAspectRatio="xMidYMid slice" role="presentation">
      <defs>
        <linearGradient id={grad} x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0%" stopColor="#38BDF8" />
          <stop offset="52%" stopColor="#818CF8" />
          <stop offset="100%" stopColor="#C084FC" />
        </linearGradient>
      </defs>
      {LANE_PATHS.map((d, i) => (
        <path key={`w${i}`} className="ai-wire" d={d} />
      ))}
      {LANE_PATHS.map((d, i) => (
        <path key={`f${i}`} className="ai-flow" d={d}
              style={{ "--i": i, stroke: `url(#${grad})` }} />
      ))}
      {LANE_NODES.map(([cx, cy], i) => (
        <circle key={`n${i}`} className="ai-node" cx={cx} cy={cy} r="2.2"
                style={{ "--i": i }} />
      ))}
    </svg>
  );
}

/* Real questions, in the words someone would actually use. Each is something
   the readings on this box can answer — a promise the product keeps, not a
   slogan. */
const QUESTIONS = [
  "why this video call keeps breaking up",
  "which link is carrying the most right now",
  "what changed on this box recently",
  "whether anything here is about to fail",
  "how concealment is set up, control by control",
];

/* What she actually is, in three lines. Each one is a property of the build,
   not a claim about it: the tools are read-only, mutating tools record a
   proposal the console applies under the operator's own session, and the tour
   walks them to the control rather than working it for them. */
const PILLARS = [
  {
    title: "Reads this box",
    body: "Live readings from this edge — links, tunnel, traffic classes — so the answer is about your network, not networks in general.",
  },
  {
    title: "Proposes, never applies",
    body: "Every change arrives as something you confirm. She cannot write to the appliance; you do, from your own session.",
  },
  {
    title: "Shows you where it lives",
    body: "She will walk you to the control on the panel and explain it there, so you learn the console while you use it.",
  },
];

function Styles() {
  return (
    <style>{`
.ai-root{
  position:fixed;inset:0;z-index:5000;
  --cyan:#38BDF8;--indigo:#818CF8;--violet:#C084FC;
  --ink:#EAECF5;--dim:#8A90A6;
  font-family:'Inter',ui-sans-serif,system-ui,-apple-system,sans-serif;
  display:grid;place-items:center;overflow:hidden;
  opacity:1;
}
.ai-root *{box-sizing:border-box}

/* ── The conversation, pushed back ───────────────────────────────────────── */
.ai-scrim{
  position:absolute;inset:0;border:0;padding:0;cursor:default;
  /* Near-opaque, not translucent: at 86% the conversation's own hero showed
     through and read as a second wordmark ghosted under this one. The blur is
     what keeps it feeling like the same screen pushed back rather than a new
     one dropped on top. */
  background:radial-gradient(125% 95% at 50% 42%,rgba(7,8,11,.975),#07080B 78%);
  -webkit-backdrop-filter:blur(14px) saturate(.8);backdrop-filter:blur(14px) saturate(.8);
  /* Slow, and concurrent with the travel rather than ahead of it: the page
     recedes while the mark grows, so there is never a black frame between the
     two. */
  opacity:0;transition:opacity .95s ease-out;
}
.ai-root.is-launch .ai-scrim{opacity:0}
.ai-root:not(.is-launch):not(.is-closing) .ai-scrim{opacity:1}
.ai-root.is-closing .ai-scrim{opacity:0;transition:opacity .5s ease-in}

/* ── Ambient fields ──────────────────────────────────────────────────────── */
.ai-field{
  position:absolute;width:78vmax;height:78vmax;border-radius:50%;pointer-events:none;
  filter:blur(90px);opacity:0;transition:opacity 1.1s ease-out;
}
.ai-field--a{
  background:radial-gradient(circle,rgba(56,189,248,.20),transparent 62%);
  top:-30vmax;left:-22vmax;animation:ai-drift-a 34s ease-in-out infinite;
}
.ai-field--b{
  background:radial-gradient(circle,rgba(192,132,252,.18),transparent 62%);
  bottom:-34vmax;right:-24vmax;animation:ai-drift-b 41s ease-in-out infinite;
}
.ai-root.is-bloom .ai-field,.ai-root.is-reveal .ai-field{opacity:1}
.ai-root.is-closing .ai-field{opacity:0;transition:opacity .45s ease-in}
@keyframes ai-drift-a{0%,100%{transform:none}50%{transform:translate3d(6vmax,4vmax,0) scale(1.08)}}
@keyframes ai-drift-b{0%,100%{transform:none}50%{transform:translate3d(-5vmax,-5vmax,0) scale(1.06)}}

/* ── The lanes ───────────────────────────────────────────────────────────── */
.ai-lanes{
  position:absolute;top:0;bottom:0;width:clamp(180px,25vw,380px);
  pointer-events:none;z-index:1;
  opacity:0;transition:opacity 1.4s ease-out;
  /* Faded at the inner edge so they never compete with the wordmark. */
  -webkit-mask-image:linear-gradient(90deg,#000 25%,transparent 96%);
          mask-image:linear-gradient(90deg,#000 25%,transparent 96%);
}
.ai-lanes--l{left:0}
.ai-lanes--r{right:0;transform:scaleX(-1)}
.ai-lanes svg{width:100%;height:100%;display:block}
.ai-root.is-bloom .ai-lanes,.ai-root.is-reveal .ai-lanes{opacity:1}
.ai-root.is-closing .ai-lanes{opacity:0;transition:opacity .4s ease-in}

.ai-wire{fill:none;stroke:rgba(148,163,184,.10);stroke-width:1}
/* The stream. Long gaps and short dashes read as discrete packets rather than
   as a marching-ants border. */
.ai-flow{
  fill:none;stroke-width:1.7;stroke-linecap:round;
  stroke-dasharray:2.5 38;
  opacity:.34;
  animation:
    ai-flow 13s linear infinite,
    ai-hot 26s ease-in-out infinite;
  animation-delay:calc(var(--i) * -3.1s), calc(var(--i) * -6.5s);
}
@keyframes ai-flow{to{stroke-dashoffset:-405}}
/* Which lane is carrying moves from one to the next, and is never on two at
   once — that is the whole point of it. */
@keyframes ai-hot{
  0%,64%,100%{opacity:.28}
  78%{opacity:.95}
}
.ai-node{
  fill:#818CF8;opacity:.22;
  animation:ai-nodepulse 6.5s ease-in-out infinite;
  animation-delay:calc(var(--i) * -1.05s);
}
@keyframes ai-nodepulse{0%,100%{opacity:.18}50%{opacity:.62}}

/* ── The mark: one object from header to halo ────────────────────────────── */
.ai-orb{
  position:absolute;left:50%;top:50%;width:var(--orb);height:var(--orb);
  margin:calc(var(--orb) / -2) 0 0 calc(var(--orb) / -2);
  display:grid;place-items:center;pointer-events:none;
  transform:translate3d(var(--home-x),var(--home-y),0) scale(var(--home-s));
  /* Leaves immediately and lands softly. Anything with a lazy first control
     point sits visibly still for a third of a second after the press, which
     reads as the click not having registered. */
  transition:transform 1.25s cubic-bezier(.2,.5,.2,1);
}
/* Centre stage, and it stays there — the reveal is composed around it. */
.ai-root.is-travel .ai-orb,
.ai-root.is-bloom .ai-orb,
.ai-root.is-reveal .ai-orb{transform:translate3d(0,0,0) scale(1)}
/* Up behind the wordmark and opened out, so the name rises out of the halo
   instead of the halo crossing the cards underneath it. */
/* Sized to frame the identity block — eyebrow and wordmark — and no larger. At
   1.5 its lower arc cut across the tops of the three cards, and read as a stray
   circle behind them rather than as light behind the name.

   Lift and pop are variables so a shorter screen can retune the numbers without
   restating the transform: re-declaring the whole property in a media block is
   what left the orb sitting at identity on a laptop. */
.ai-root{--lift:-20vh;--pop:1.05}
/* Opening the detail grows the column downward, so the whole block rides up.
   The halo has to ride with it or its arc drifts across the tagline. */
.ai-root.has-more{--lift:-31vh}
.ai-root.is-reveal .ai-orb{
  transform:translate3d(0,var(--lift),0) scale(var(--pop));
  transition:transform 1.25s cubic-bezier(.3,.8,.25,1);
}
/* Home again, by the road it came. */
.ai-root.is-closing .ai-orb{
  transform:translate3d(var(--home-x),var(--home-y),0) scale(var(--home-s));
  transition:transform .58s cubic-bezier(.5,0,.2,1);
}

/* The ring itself — the header's conic gradient, drawn large.
   One rotation carries it the whole way and lands on 3 whole turns, so the
   idle spin that takes over begins exactly where this ended. */
.ai-ring{
  position:absolute;inset:0;border-radius:50%;
  background:conic-gradient(from 210deg,var(--cyan),var(--indigo),var(--violet),var(--cyan));
  -webkit-mask:radial-gradient(circle,transparent 57%,#000 60%);
          mask:radial-gradient(circle,transparent 57%,#000 60%);
  animation:ai-turn 2.35s cubic-bezier(.55,.03,.3,1) both;
  transition:filter .8s ease-out,opacity .8s ease-out,transform .8s ease-out;
}
@keyframes ai-turn{from{transform:rotate(0deg)}to{transform:rotate(1080deg)}}

/* The dissolve: the crisp ring softens into the halo the words sit on. It is
   never removed — losing it here would break the thread the eye is following. */
.ai-root.is-bloom .ai-ring{filter:blur(13px);opacity:.5}
.ai-root.is-reveal .ai-ring{filter:blur(24px);opacity:.36}
.ai-root.is-reveal .ai-ring{
  animation:ai-spin 26s linear infinite;
}
@keyframes ai-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
.ai-root.is-closing .ai-ring{filter:none;opacity:1;animation:none;transition:filter .4s,opacity .4s}

/* The glow under the ring, which becomes the page's light source. */
.ai-halo{
  position:absolute;inset:-42%;border-radius:50%;pointer-events:none;
  background:radial-gradient(circle,rgba(129,140,248,.34),rgba(129,140,248,.10) 44%,transparent 68%);
  opacity:0;transform:scale(.55);
  transition:opacity .85s ease-out,transform .95s cubic-bezier(.2,.8,.2,1);
}
.ai-root.is-bloom .ai-halo,.ai-root.is-reveal .ai-halo{opacity:1;transform:scale(1)}
.ai-root.is-closing .ai-halo{opacity:0;transform:scale(.55);transition:opacity .35s,transform .4s}

/* Three rings leaving the orb once, at the moment it opens. The stagger is
   what makes it read as a bloom rather than a pulse. */
.ai-orbit{
  position:absolute;inset:0;border-radius:50%;pointer-events:none;
  border:1px solid rgba(129,140,248,.42);opacity:0;
}
.ai-root.is-bloom .ai-orbit{animation:ai-ripple 1.5s cubic-bezier(.2,.7,.3,1) forwards}
.ai-orbit--2{animation-delay:.16s !important;border-color:rgba(56,189,248,.34)}
.ai-orbit--3{animation-delay:.32s !important;border-color:rgba(192,132,252,.30)}
@keyframes ai-ripple{
  0%{opacity:.85;transform:scale(1)}
  100%{opacity:0;transform:scale(2.5)}
}

.ai-logo{
  position:relative;width:38%;height:38%;object-fit:contain;
  filter:drop-shadow(0 4px 18px rgba(129,140,248,.5));
  transition:opacity .7s ease-out,transform .7s ease-out,filter .7s ease-out,width .7s ease-out,height .7s ease-out;
}
/* The mark stays inside its ring and recedes with it — the same softening the
   ring gets, not a disappearance. It used to lift out and vanish, which left
   the circle empty and broke the object the whole sequence is following. The
   blur is gentler than the ring's 24px: a thin W dissolves entirely at that
   radius, where the ring only softens. */
/* Larger than it was in the ring, because the wordmark is opaque and sits on
   top of it: at 38% only the crown of the W cleared "Ada" and read as a stray
   shape. Bigger, it frames the type instead of hiding behind it. */
.ai-root.is-reveal .ai-logo{width:64%;height:64%;opacity:.16;filter:blur(2.5px)}
.ai-root.is-closing .ai-logo{opacity:1;filter:none;transition:opacity .3s,filter .3s}

/* ── The words ───────────────────────────────────────────────────────────── */
.ai-content{
  position:relative;z-index:2;display:flex;flex-direction:column;align-items:center;
  text-align:center;padding:24px;max-width:1000px;width:100%;
  pointer-events:none;
}
.ai-root.is-reveal .ai-content{pointer-events:auto}
.ai-content > *{opacity:0}
/* Everything rises on its own delay, set inline. One curve for all of it, so
   the stagger reads as a single movement with parts rather than nine cues. */
.ai-root.is-reveal .ai-content > *{
  animation:ai-rise .62s cubic-bezier(.2,.7,.2,1) both;
  animation-delay:var(--d,0ms);
}
@keyframes ai-rise{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}
.ai-root.is-closing .ai-content{opacity:0;transition:opacity .22s ease-in}

/* The brand as a lockup, not a caption.
 *
 * On one line at this size the string is simply wider than the screen — seven
 * letters plus twelve more, set near the wordmark's size, runs past 1600px. So
 * it stacks: WEAVIAN takes the display size, EDGE CONSOLE sits under it as the
 * qualifier it is. Together they still read as one line of brand above the
 * name, which is the thing that had to get bigger. */
.ai-eyebrow{
  margin:0 0 4px;display:flex;flex-direction:column;align-items:center;
  text-transform:uppercase;
}
/* Just under the wordmark — near enough to belong to it, far enough that "Ada"
   is still what the page is introducing. Tracking is nearly off: letterspacing
   set for caption type tears a display word apart. */
.ai-eyebrow-brand{
  font-size:clamp(60px,11.2vw,150px);line-height:1;font-weight:800;letter-spacing:.005em;
  color:#F4F6FF;
  text-shadow:0 4px 34px rgba(129,140,248,.42);
}
.ai-eyebrow-rest{
  margin-top:.55em;font-size:clamp(13px,1.35vw,19px);font-weight:600;letter-spacing:.42em;
  /* Indent by the tracking so the tracked line stays optically centred — the
     trailing space after the last letter is real and pushes it left. */
  text-indent:.42em;
  color:rgba(199,210,254,.66);
}
/* Sized so the wordmark clears the orb rather than sitting on its middle. */
.ai-name{
  margin:0;font-size:clamp(84px,13.5vw,180px);line-height:.9;font-weight:800;letter-spacing:-.045em;
  background:linear-gradient(103deg,var(--cyan) 4%,var(--indigo) 46%,var(--violet) 92%);
  -webkit-background-clip:text;background-clip:text;color:transparent;
  filter:drop-shadow(0 10px 40px rgba(129,140,248,.30));
}
.ai-tagline{
  margin:18px 0 0;font-size:clamp(21px,2.9vw,34px);font-weight:400;color:var(--ink);letter-spacing:-.015em;
}
.ai-tagline span{
  background:linear-gradient(100deg,var(--cyan),var(--indigo) 55%,var(--violet));
  -webkit-background-clip:text;background-clip:text;color:transparent;font-weight:600;
}
.ai-rule{
  width:min(420px,62%);height:1px;margin:30px 0 4px;
  background:linear-gradient(90deg,transparent,rgba(129,140,248,.55),transparent);
}

/* ── The ask line ────────────────────────────────────────────────────────── */
.ai-asks{
  margin:26px 0 0;display:flex;flex-direction:column;align-items:center;gap:6px;
  min-height:76px;
}
.ai-asks-lead{
  font-size:13px;font-weight:600;letter-spacing:.2em;text-transform:uppercase;
  color:rgba(138,144,166,.85);
}
/* Fixed height so the block below never moves as the questions turn over. */
.ai-asks-rota{
  display:block;position:relative;height:44px;line-height:44px;
  font-size:clamp(19px,2.5vw,29px);letter-spacing:-.015em;
}
.ai-asks-q{
  display:inline-block;
  background:linear-gradient(100deg,var(--cyan),var(--indigo) 52%,var(--violet));
  -webkit-background-clip:text;background-clip:text;color:transparent;font-weight:600;
  animation:ai-ask-in .6s cubic-bezier(.2,.7,.2,1) both;
}
@keyframes ai-ask-in{
  from{opacity:0;transform:translateY(11px);filter:blur(5px)}
  to{opacity:1;transform:none;filter:none}
}

/* ── The detail, for whoever wants it ────────────────────────────────────── */
.ai-more{
  margin-top:20px;display:inline-flex;align-items:center;gap:7px;cursor:pointer;
  background:none;border:0;font:inherit;font-size:13.5px;font-weight:500;
  color:rgba(138,144,166,.95);padding:6px 4px;
}
.ai-more:hover{color:var(--ink)}
.ai-more:focus-visible{outline:2px solid #A5B4FC;outline-offset:3px;border-radius:6px}
.ai-more svg{transition:transform .25s cubic-bezier(.2,.7,.2,1)}
.ai-more.is-open svg{transform:rotate(180deg)}

.ai-pillars{
  list-style:none;margin:18px 0 0;padding:0;display:grid;gap:14px;width:100%;
  grid-template-columns:repeat(3,minmax(0,1fr));
  animation:ai-rise .5s cubic-bezier(.2,.7,.2,1) both;
}
.ai-pillar{
  display:flex;flex-direction:column;align-items:flex-start;gap:6px;text-align:left;
  padding:16px 16px 17px;border-radius:14px;
  border:1px solid rgba(255,255,255,.07);
  background:linear-gradient(180deg,rgba(255,255,255,.045),rgba(255,255,255,.012));
  opacity:0;
}
.ai-pillar{animation:ai-rise .5s cubic-bezier(.2,.7,.2,1) both;animation-delay:var(--d,0ms)}
.ai-pillar-no{
  font-size:10.5px;font-weight:700;letter-spacing:.16em;color:rgba(129,140,248,.85);
}
.ai-pillar strong{font-size:15.5px;font-weight:650;color:var(--ink);letter-spacing:-.01em}
.ai-pillar-body{font-size:14px;line-height:1.55;color:var(--dim)}

.ai-foot{grid-column:1/-1;margin:6px 0 0;font-size:13.5px;color:rgba(138,144,166,.9);text-align:center;list-style:none}

.ai-actions{margin-top:30px;display:flex;gap:10px;align-items:center}
.ai-go{
  position:relative;overflow:hidden;display:inline-flex;align-items:center;gap:10px;
  padding:16px 34px;border-radius:999px;cursor:pointer;
  font:inherit;font-size:17px;font-weight:650;letter-spacing:.01em;color:#0A0B10;
  border:1px solid rgba(255,255,255,.24);
  background:linear-gradient(120deg,var(--cyan) 0%,var(--indigo) 50%,var(--violet) 100%);
  box-shadow:0 12px 34px -12px rgba(129,140,248,.95),inset 0 1px 0 rgba(255,255,255,.4);
  transition:transform .18s,box-shadow .18s,filter .18s;
}
.ai-go:hover{transform:translateY(-1px);filter:saturate(1.1) brightness(1.04);
  box-shadow:0 16px 40px -12px rgba(192,132,252,1),inset 0 1px 0 rgba(255,255,255,.46)}
.ai-go:active{transform:translateY(0)}
.ai-go:focus-visible{outline:2px solid #A5B4FC;outline-offset:3px}
.ai-go > span:not(.ai-go-sheen),.ai-go svg{position:relative;z-index:1}
.ai-go-sheen{
  position:absolute;top:0;left:-140%;width:55%;height:100%;
  background:linear-gradient(100deg,transparent,rgba(255,255,255,.5),transparent);
  animation:ai-sheen 4.6s ease-in-out infinite;animation-delay:1.2s;
}
.ai-go:hover .ai-go-sheen{animation-play-state:paused}
@keyframes ai-sheen{0%{left:-140%}55%{left:150%}100%{left:150%}}

.ai-skip{
  position:absolute;top:18px;right:20px;z-index:3;
  width:34px;height:34px;display:grid;place-items:center;cursor:pointer;
  border-radius:50%;border:1px solid rgba(255,255,255,.10);
  background:rgba(255,255,255,.04);color:#8A90A6;
  opacity:0;transition:opacity .4s ease-out .5s,color .15s,border-color .15s,background .15s;
}
.ai-root:not(.is-launch):not(.is-closing) .ai-skip{opacity:1}
.ai-skip:hover{color:#E8EAF2;border-color:rgba(255,255,255,.22);background:rgba(255,255,255,.08)}
.ai-skip:focus-visible{outline:2px solid #A5B4FC;outline-offset:3px}

/* Shorter screens: the gap between the halo and the cards is a fraction of the
   height, so on a laptop it closed up and the arc landed on the card edges
   again. The halo gives ground before the type does. */
@media (max-height:880px){
  .ai-root{--lift:-23vh;--pop:.86}
  .ai-root.has-more{--lift:-34vh}
  .ai-pillars{margin-top:30px}
}

@media (max-width:900px){
  .ai-lanes{display:none}
}

@media (max-width:820px){
  .ai-pillars{grid-template-columns:1fr;gap:10px}
  .ai-pillar{padding:13px 14px}
  .ai-content{padding:20px 16px}
}
/* Below this the orb and the type fight for the same space; the type wins. */
@media (max-height:820px){
  .ai-name{font-size:clamp(60px,10vw,96px)}
  .ai-eyebrow-brand{font-size:clamp(44px,8vw,96px)}
  .ai-eyebrow-rest{font-size:clamp(11px,1.1vw,14px)}
  .ai-asks{margin-top:16px;min-height:64px}
  .ai-asks-rota{height:38px;line-height:38px;font-size:clamp(17px,2.2vw,23px)}
  .ai-actions{margin-top:20px}
}

@media (prefers-reduced-motion:reduce){
  .ai-orb,.ai-ring,.ai-halo,.ai-orbit,.ai-logo,.ai-field,.ai-content > *,
  .ai-pillar,.ai-pillars,.ai-asks-q,.ai-go-sheen,.ai-flow,.ai-node{
    animation:none !important;transition:none !important;
  }
  /* No travel: the orb is simply where it ends up, and the words are simply
     there. The page still says what it has to say. */
  .ai-orb{transform:translate3d(0,var(--lift),0) scale(var(--pop)) !important}
  .ai-ring{filter:blur(24px);opacity:.36}
  .ai-halo{opacity:1;transform:scale(1)}
  .ai-logo{width:64%;height:64%;opacity:.16;filter:blur(2.5px)}
  .ai-field,.ai-lanes{opacity:1}
  .ai-flow{opacity:.3}
  .ai-node{opacity:.3}
  .ai-content > *,.ai-pillar,.ai-pillars,.ai-asks-q{opacity:1}
  .ai-scrim{opacity:1;transition:none}
}
`}</style>
  );
}
